import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  GraduationCap, Plus, Award, CheckCircle2, PlayCircle, Loader2, Pencil, FileText,
  BookOpen, AlertCircle, Clock, Circle, AlertTriangle, Search, Calendar, ShieldCheck,
  Layers, ListChecks,
} from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { academyAPI } from '@/lib/api';
import { getErrorMessage } from '@/lib/utils';

const STATUS = {
  not_started: { label: 'Not Started', cls: 'bg-slate-100 text-slate-600 border border-slate-200', fill: 'bg-slate-400', Icon: Circle },
  in_progress: { label: 'In Progress', cls: 'bg-teal-50 text-teal-700 border border-teal-200', fill: 'bg-teal-600', Icon: PlayCircle },
  completed: { label: 'Completed', cls: 'bg-emerald-50 text-emerald-700 border border-emerald-200', fill: 'bg-emerald-600', Icon: CheckCircle2 },
  failed: { label: 'Failed', cls: 'bg-rose-50 text-rose-700 border border-rose-200', fill: 'bg-rose-500', Icon: AlertTriangle },
  expired: { label: 'Expired', cls: 'bg-amber-50 text-amber-800 border border-amber-300', fill: 'bg-amber-500', Icon: Clock },
};

const ACTION_LABEL = { not_started: 'Start', in_progress: 'Resume', completed: 'Review', failed: 'Retry', expired: 'Review' };

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '');

function StatusChip({ status }) {
  const s = STATUS[status] || STATUS.not_started;
  const { Icon } = s;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${s.cls}`} data-testid={`status-chip-${status}`}>
      <Icon size={11} /> {s.label}
    </span>
  );
}

function CourseCard({ c, isAdmin, onOpen, onEdit }) {
  const s = STATUS[c.progress_status] || STATUS.not_started;
  const dueSoon = c.due_date && c.progress_status !== 'completed';
  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm hover:shadow-md transition-shadow flex flex-col overflow-hidden" data-testid={`academy-course-${c.course_id}`}>
      <div className="h-20 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 px-3 py-2 flex flex-col justify-between relative">
        <div className="flex items-center justify-between">
          <span className="inline-flex items-center gap-1 text-[10px] font-medium text-slate-300">
            <GraduationCap size={13} /> {c.category || 'General'}
          </span>
          {c.required ? (
            <span className="rounded-full bg-rose-500/90 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white" data-testid={`required-chip-${c.course_id}`}>Required</span>
          ) : (
            <span className="rounded-full bg-white/10 px-2 py-0.5 text-[9px] font-medium text-slate-300">Optional</span>
          )}
        </div>
        <div className="flex items-center justify-between">
          {isAdmin ? (
            <Badge className={c.status === 'published' ? 'bg-emerald-500/20 text-emerald-200 text-[10px] border-0' : 'bg-white/10 text-slate-300 text-[10px] border-0'}>
              {c.status === 'published' ? 'Published' : 'Draft'}
            </Badge>
          ) : <StatusChip status={c.progress_status} />}
          <div className="flex items-center gap-1.5">
            {c.gives_certificate && <FileText size={13} className="text-amber-300/80" title="Certificate available" />}
            <Award size={13} className="text-amber-300/80" title="Badge available" />
          </div>
        </div>
      </div>

      <div className="p-3 flex flex-col flex-1 gap-2">
        <div>
          <h4 className="font-semibold text-slate-800 text-sm leading-tight line-clamp-1">{c.title}</h4>
          <p className="text-xs text-slate-500 line-clamp-2 min-h-[32px] mt-0.5">{c.description || 'No description provided.'}</p>
        </div>

        <div className="flex items-center gap-3 text-[11px] text-slate-500">
          <span className="inline-flex items-center gap-1"><Layers size={12} /> {c.modules_count} mod</span>
          <span className="inline-flex items-center gap-1"><BookOpen size={12} /> {c.lessons_count} lessons</span>
          {c.has_exam && <span className="inline-flex items-center gap-1 text-indigo-600"><ListChecks size={12} /> Exam</span>}
        </div>

        {!isAdmin && (
          <div>
            <div className="flex items-center justify-between text-[10px] text-slate-400 mb-1">
              <span>Progress</span><span className="font-semibold text-slate-600">{c.progress_pct}%</span>
            </div>
            <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
              <div className={`h-full rounded-full ${s.fill} transition-all`} style={{ width: `${c.progress_pct}%` }} />
            </div>
          </div>
        )}

        <div className="flex items-center justify-between text-[10px] text-slate-400 pt-1 border-t border-slate-100 mt-auto">
          <span className="inline-flex items-center gap-1"><ShieldCheck size={11} /> Pass {c.pass_score}%</span>
          {c.due_date && <span className={`inline-flex items-center gap-1 ${dueSoon ? 'text-amber-600 font-medium' : ''}`}><Calendar size={11} /> {fmtDate(c.due_date)}</span>}
        </div>

        <div className="flex gap-2 pt-1">
          <Button size="sm" className="flex-1 h-8" onClick={() => onOpen(c)} data-testid={`academy-open-${c.course_id}`}>
            <PlayCircle size={14} className="mr-1" /> {isAdmin ? 'Preview' : (ACTION_LABEL[c.progress_status] || 'Start')}
          </Button>
          {isAdmin && (
            <Button size="sm" variant="outline" className="h-8" onClick={() => onEdit(c)} data-testid={`academy-edit-${c.course_id}`}>
              <Pencil size={14} />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function StatBox({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 flex items-center gap-3" data-testid={`academy-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${accent}`}><Icon size={17} /></div>
      <div>
        <div className="text-lg font-bold text-slate-900 leading-none">{value}</div>
        <div className="text-[11px] text-slate-500 mt-0.5">{label}</div>
      </div>
    </div>
  );
}

export default function AcademyPage() {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [badges, setBadges] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('my-courses');
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, b] = await Promise.all([academyAPI.listCourses(), academyAPI.myBadges()]);
      setCourses(c.data.items || []);
      setIsAdmin(!!c.data.is_admin);
      setBadges(b.data.items || []);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Could not load Academy'));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const createCourse = async () => {
    try {
      const r = await academyAPI.createCourse({ title: 'Untitled course' });
      navigate(`/academy/manage/${r.data.course_id}`);
    } catch (e) { toast.error(getErrorMessage(e, 'Could not create course')); }
  };

  const certificates = useMemo(() => badges.filter((b) => b.gives_certificate && b.certificate_id), [badges]);
  const categories = useMemo(() => Array.from(new Set(courses.map((c) => c.category).filter(Boolean))).sort(), [courses]);

  const nextDeadline = useMemo(() => {
    const dates = courses.filter((c) => c.due_date && c.progress_status !== 'completed').map((c) => c.due_date).sort();
    return dates[0] ? fmtDate(dates[0]) : '—';
  }, [courses]);

  const filtered = useMemo(() => {
    let list = courses;
    if (tab === 'required') list = list.filter((c) => c.required);
    else if (tab === 'in-progress') list = list.filter((c) => c.progress_status === 'in_progress');
    else if (tab === 'completed') list = list.filter((c) => c.progress_status === 'completed');
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((c) => (c.title || '').toLowerCase().includes(q) || (c.description || '').toLowerCase().includes(q));
    }
    if (category !== 'all') list = list.filter((c) => c.category === category);
    return list;
  }, [courses, tab, search, category]);

  const counts = useMemo(() => ({
    'my-courses': courses.length,
    required: courses.filter((c) => c.required).length,
    'in-progress': courses.filter((c) => c.progress_status === 'in_progress').length,
    completed: courses.filter((c) => c.progress_status === 'completed').length,
    certificates: certificates.length,
    badges: badges.length,
  }), [courses, certificates, badges]);

  const TABS = [
    { id: 'my-courses', label: 'My Courses', Icon: BookOpen },
    { id: 'required', label: 'Required', Icon: AlertCircle },
    { id: 'in-progress', label: 'In Progress', Icon: Clock },
    { id: 'completed', label: 'Completed', Icon: CheckCircle2 },
    { id: 'certificates', label: 'Certificates', Icon: FileText },
    { id: 'badges', label: 'Badges', Icon: Award },
  ];

  const renderGrid = (list, emptyMsg) => (
    list.length === 0 ? (
      <Card><CardContent className="py-14 text-center text-sm text-slate-500" data-testid="academy-empty">
        <GraduationCap size={26} className="mx-auto mb-2 text-slate-300" />{emptyMsg}
      </CardContent></Card>
    ) : (
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="academy-course-grid">
        {list.map((c) => (
          <CourseCard key={c.course_id} c={c} isAdmin={isAdmin}
            onOpen={(x) => navigate(`/academy/${x.course_id}`)}
            onEdit={(x) => navigate(`/academy/manage/${x.course_id}`)} />
        ))}
      </div>
    )
  );

  return (
    <div data-testid="academy-page">
      <PageHeader
        title="Academy"
        description="Structured courses, exams and certifications — track your progress and earn badges."
        breadcrumbs={[{ label: 'Academy', href: '/academy' }]}
        actions={isAdmin ? (
          <Button onClick={createCourse} data-testid="academy-new-course"><Plus size={16} className="mr-1" /> New course</Button>
        ) : null}
      />
      <div className="page-content space-y-5">
        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="animate-spin text-slate-400" /></div>
        ) : (
          <>
            {!isAdmin && (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="academy-summary">
                <StatBox icon={BookOpen} label="Assigned" value={courses.length} accent="bg-slate-100 text-slate-700" />
                <StatBox icon={CheckCircle2} label="Completed" value={counts.completed} accent="bg-emerald-100 text-emerald-700" />
                <StatBox icon={Award} label="Certificates" value={certificates.length} accent="bg-amber-100 text-amber-700" />
                <StatBox icon={Calendar} label="Next Deadline" value={nextDeadline} accent="bg-teal-100 text-teal-700" />
              </div>
            )}

            <Tabs value={tab} onValueChange={setTab}>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <TabsList className="flex-wrap h-auto" data-testid="academy-tabs">
                  {TABS.map(({ id, label, Icon }) => (
                    <TabsTrigger key={id} value={id} className="gap-1.5 text-xs" data-testid={`academy-tab-${id}`}>
                      <Icon size={14} /> {label}
                      {counts[id] > 0 && <span className="ml-1 rounded-full bg-slate-200 px-1.5 text-[10px] font-semibold text-slate-700">{counts[id]}</span>}
                    </TabsTrigger>
                  ))}
                </TabsList>
                {['my-courses', 'required', 'in-progress', 'completed'].includes(tab) && (
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                      <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search courses" className="pl-8 h-9 w-44" data-testid="academy-search" />
                    </div>
                    {categories.length > 0 && (
                      <select value={category} onChange={(e) => setCategory(e.target.value)} className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-700" data-testid="academy-category-filter">
                        <option value="all">All categories</option>
                        {categories.map((cat) => <option key={cat} value={cat}>{cat}</option>)}
                      </select>
                    )}
                  </div>
                )}
              </div>

              <TabsContent value="my-courses" className="mt-4">{renderGrid(filtered, isAdmin ? 'No courses yet. Create your first course.' : 'No courses assigned to you yet.')}</TabsContent>
              <TabsContent value="required" className="mt-4">{renderGrid(filtered, 'No required courses right now.')}</TabsContent>
              <TabsContent value="in-progress" className="mt-4">{renderGrid(filtered, 'Nothing in progress. Start a course to see it here.')}</TabsContent>
              <TabsContent value="completed" className="mt-4">{renderGrid(filtered, 'No completed courses yet.')}</TabsContent>

              <TabsContent value="certificates" className="mt-4">
                {certificates.length === 0 ? (
                  <Card><CardContent className="py-14 text-center text-sm text-slate-500" data-testid="academy-certs-empty">
                    <FileText size={26} className="mx-auto mb-2 text-slate-300" />No certificates earned yet.
                  </CardContent></Card>
                ) : (
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="academy-certificates">
                    {certificates.map((b) => (
                      <div key={b.certificate_id} className="rounded-xl border border-amber-300/80 bg-gradient-to-br from-amber-50 to-amber-100/60 p-4 shadow-sm shadow-amber-200/50" data-testid={`certificate-card-${b.course_id}`}>
                        <div className="flex items-start justify-between">
                          <div className="w-11 h-11 rounded-lg bg-amber-400 text-white flex items-center justify-center"><FileText size={20} /></div>
                          <Badge className="bg-amber-200 text-amber-900 text-[10px] border-0">{b.best_score}%</Badge>
                        </div>
                        <div className="mt-3 text-sm font-semibold text-amber-900 line-clamp-2">{b.title}</div>
                        <div className="text-[11px] text-amber-700 mt-0.5">Issued {fmtDate(b.passed_at)}</div>
                        <Button size="sm" variant="outline" className="w-full mt-3 border-amber-300 text-amber-800 hover:bg-amber-100" onClick={() => navigate(`/academy/certificate/${b.certificate_id}`)} data-testid={`academy-cert-${b.course_id}`}>
                          <FileText size={13} className="mr-1" /> View certificate
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="badges" className="mt-4">
                {badges.length === 0 ? (
                  <Card><CardContent className="py-14 text-center text-sm text-slate-500" data-testid="academy-badges-empty">
                    <Award size={26} className="mx-auto mb-2 text-slate-300" />No badges earned yet. Complete a course to earn one.
                  </CardContent></Card>
                ) : (
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="academy-badges">
                    {badges.map((b) => (
                      <div key={b.course_id} className="rounded-xl border border-amber-300/80 bg-gradient-to-br from-amber-50 to-amber-100/60 p-4 flex items-center gap-3 shadow-sm shadow-amber-200/50" data-testid={`badge-card-${b.course_id}`}>
                        <div className="w-12 h-12 rounded-full bg-amber-400 text-white flex items-center justify-center shrink-0"><Award size={24} /></div>
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-amber-900 truncate">{b.title}</div>
                          <div className="text-[11px] text-amber-700">Earned {fmtDate(b.passed_at)} · {b.best_score}%</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </>
        )}
      </div>
    </div>
  );
}
